import React, { useState, useEffect, useMemo } from "react";
import { Box, Typography, Card, Chip, CircularProgress } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventNoteIcon from "@mui/icons-material/EventNote";
import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import EmailIcon from "@mui/icons-material/Email";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { motion } from "framer-motion";
import api from "../../../utils/api";

const timelineTypeConfig = {
  architecturalRequest: {
    color: "#1E88E5",
    icon: AssignmentTurnedInIcon,
    label: "Request",
  },
  comment: {
    color: "#43A047",
    icon: ChatBubbleIcon,
    label: "Comment",
  },
  event: {
    color: "#8E24AA",
    icon: EventNoteIcon,
    label: "Event",
  },
  email: {
    color: "#FB8C00",
    icon: EmailIcon,
    label: "Email",
  },
};

const ArchitecturalRequestTimeline = ({ architectural_request_id }) => {
  const [timelineData, setTimelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  useEffect(() => {
    const fetchTimeline = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(
          `/architecturalRequestTimelineExternal/${architectural_request_id}`
        );
        setTimelineData(data);
      } catch (err) {
        console.error("Failed to fetch timeline:", err);
        setError("Failed to load timeline data.");
      } finally {
        setLoading(false);
      }
    };

    if (architectural_request_id) {
      fetchTimeline();
    }
  }, [architectural_request_id]);

  const combinedTimeline = useMemo(() => {
    if (!timelineData || !timelineData.architecturalRequest) return [];

    const { architecturalRequest, comments = [], events = [], emails = [] } =
      timelineData;

    const timeline = [
      {
        type: "architecturalRequest",
        title: "Architectural Request Submitted",
        time: architecturalRequest.created_at,
      },

      ...comments.map(({ comment_text, sent_at }) => ({
        type: "comment",
        title: comment_text,
        time: sent_at || new Date().toISOString(),
      })),

      ...events.map(({ title, created_at }) => ({
        type: "event",
        title,
        time: created_at || new Date().toISOString(),
      })),

      ...emails.map(({ subject, sent_at }) => ({
        type: "email",
        title: subject,
        time: sent_at || new Date().toISOString(),
      })),

      ...(architecturalRequest.reviewed_at
        ? [
            {
              type: "architecturalRequest",
              title: "Request Reviewed",
              time: architecturalRequest.reviewed_at,
            },
          ]
        : []),
    ];

    return timeline.sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [timelineData]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "180px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: "center" }}>
        {error}
      </Typography>
    );
  }

  if (combinedTimeline.length === 0) {
    return (
      <Card sx={{ ...pageCardSx, p: 2.5, textAlign: "center" }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: "#223142" }}>
          No timeline activity found
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.5 }}>
          Timeline activity will appear here as the request is updated.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          mb: 1.5,
          fontWeight: 700,
          color: "#223142",
        }}
      >
        Architectural Request Timeline
      </Typography>

      <Box
        sx={{
          position: "relative",
          pl: { xs: 2, sm: 2.5 },
          "&::before": {
            content: '""',
            position: "absolute",
            left: { xs: "9px", sm: "11px" },
            top: 8,
            bottom: 8,
            width: "2px",
            backgroundColor: "rgba(47,99,173,0.12)",
          },
        }}
      >
        {combinedTimeline.map(({ type, title, time }, index) => {
          const config =
            timelineTypeConfig[type] || timelineTypeConfig.architecturalRequest;
          const IconComponent = config.icon;

          return (
            <motion.div
              key={`${type}-${time}-${index}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.28, delay: index * 0.03 }}
            >
              <Box sx={{ position: "relative", mb: 1.25 }}>
                <Box
                  sx={{
                    position: "absolute",
                    left: { xs: "-15px", sm: "-18px" },
                    top: 18,
                    width: { xs: 14, sm: 16 },
                    height: { xs: 14, sm: 16 },
                    borderRadius: "999px",
                    backgroundColor: config.color,
                    border: "3px solid #fff",
                    boxShadow: "0 0 0 1px rgba(47,99,173,0.08)",
                    zIndex: 2,
                  }}
                />

                <Card
                  sx={{
                    borderRadius: "14px",
                    border: "1px solid rgba(47,99,173,0.08)",
                    boxShadow: "0 3px 10px rgba(0,0,0,0.04)",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                  }}
                >
                  <Box sx={{ p: { xs: 1.1, sm: 1.25 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 0.9,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 30, sm: 32 },
                            height: { xs: 30, sm: 32 },
                            borderRadius: "9px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: `${config.color}15`,
                            flexShrink: 0,
                          }}
                        >
                          <IconComponent sx={{ color: config.color, fontSize: 16 }} />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 700,
                              color: "#223142",
                              lineHeight: 1.25,
                              wordBreak: "break-word",
                              overflowWrap: "anywhere",
                            }}
                          >
                            {title}
                          </Typography>
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "flex-start", sm: "flex-end" },
                          width: { xs: "100%", sm: "auto" },
                          pl: { xs: "38px", sm: 0 },
                        }}
                      >
                        <Chip
                          size="small"
                          label={config.label}
                          sx={{
                            maxWidth: "100%",
                            height: 24,
                            borderRadius: "999px",
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            color: config.color,
                            backgroundColor: `${config.color}15`,
                            "& .MuiChip-label": {
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              px: 1,
                            },
                          }}
                        />
                      </Box>
                    </Box>

                    {type === "comment" && (
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: "10px",
                          backgroundColor: "#f9fafb",
                          border: "1px solid rgba(47,99,173,0.06)",
                          mb: 0.75,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#555",
                            fontStyle: "italic",
                            fontSize: "0.82rem",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                          }}
                        >
                          “{title}”
                        </Typography>
                      </Box>
                    )}

                    {type !== "comment" && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#223142",
                          mb: time ? 0.55 : 0,
                          fontSize: "0.82rem",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {title}
                      </Typography>
                    )}

                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.72rem",
                        color: "#8a94a6",
                        fontStyle: "italic",
                        display: "flex",
                        alignItems: "center",
                        mt: 0.25,
                        wordBreak: "break-word",
                      }}
                    >
                      <AccessTimeIcon sx={{ fontSize: 11, mr: 0.45 }} />
                      {new Date(time).toLocaleString()}
                    </Typography>
                  </Box>
                </Card>
              </Box>
            </motion.div>
          );
        })}
      </Box>

      <Legend />
    </Box>
  );
};

const Legend = () => {
  const items = ["architecturalRequest", "comment", "event", "email"];

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: "#223142", mb: 0.75 }}
      >
        Legend
      </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
        {items.map((key) => {
          const item = timelineTypeConfig[key];
          const IconComponent = item.icon;

          return (
            <Chip
              key={key}
              icon={<IconComponent sx={{ fontSize: "14px !important" }} />}
              label={item.label}
              size="small"
              sx={{
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "0.72rem",
                color: item.color,
                backgroundColor: `${item.color}15`,
              }}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default ArchitecturalRequestTimeline;